/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@material-ui/core";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import { purchaseActions } from "modules/purchase";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { getCardTokenGMO } from "utility/gmo";
import { z } from "zod/v3";

import type { TFunction } from "i18next";

type CardInputInfo = {
  cardNumber: string;
  cardExpire: {
    month: string;
    year: string;
  };
  cardHolderName: string;
  cardSecurityCode: string;
};

const createRegisterCardInfoSchema = (t: TFunction) =>
  z.object({
    cardNumber: z
      .string()
      .transform((val) => val.replace(/[\s-ー－]/g, ""))
      .pipe(
        z
          .string()
          .regex(
            /^\d{14,16}$/,
            t("streaming.liveCommerce.registerCardValidation.cardNumber.regex")
          )
      ),
    cardExpire: z
      .object({
        month: z
          .string()
          .transform((val) => val.padStart(2, "0"))
          .pipe(
            z
              .string()
              .regex(
                /^(0[1-9]|1[0-2])$/,
                t(
                  "streaming.liveCommerce.registerCardValidation.cardExpire.month.regex"
                )
              )
          ),
        year: z
          .string()
          .regex(
            /^([0-9]{2})$/,
            t(
              "streaming.liveCommerce.registerCardValidation.cardExpire.year.regex"
            )
          ),
      })
      .superRefine((data, ctx) => {
        const now = new Date();
        const currentYear = now.getFullYear() % 100;
        const currentMonth = now.getMonth() + 1;

        const expYear = parseInt(data.year, 10);
        const expMonth = parseInt(data.month, 10);

        const isExpired =
          expYear < currentYear ||
          (expYear === currentYear && expMonth < currentMonth);

        if (isExpired) {
          const errorMessage = t(
            "streaming.liveCommerce.registerCardValidation.cardExpire.superRefine"
          );
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
            path: ["month"],
          });

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
            path: ["year"],
          });
        }
      }),
    cardHolderName: z
      .string()
      .min(
        1,
        t("streaming.liveCommerce.registerCardValidation.cardHolderName.min")
      )
      .transform((val) => val.toUpperCase())
      .refine(
        (val) => /^[A-Z\s]+$/.test(val),
        t("streaming.liveCommerce.registerCardValidation.cardHolderName.refine")
      ),
    cardSecurityCode: z
      .string()
      .regex(
        /^\d{3,4}$/,
        t(
          "streaming.liveCommerce.registerCardValidation.cardSecurityCode.regex"
        )
      ),
  });

type RegisterCardInfo = z.infer<
  ReturnType<typeof createRegisterCardInfoSchema>
>;

export const StreamingLiveCommerceRegisterCreditV2: React.FC<{
  isDarkModeEnabled: boolean;
  setSelectedCardType: (type: "registered" | "new") => void;
}> = ({ isDarkModeEnabled, setSelectedCardType }) => {
  const { t } = useI18n();
  const registerCardInfoSchema = React.useMemo(
    () => createRegisterCardInfoSchema(t),
    [t]
  );
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<RegisterCardInfo>({
    resolver: zodResolver(registerCardInfoSchema),
    mode: "onChange",
  });

  const watchAllFields = watch();
  const isAnyFieldFilled = React.useMemo(() => {
    return (
      !!watchAllFields.cardNumber ||
      !!watchAllFields.cardHolderName ||
      !!watchAllFields.cardSecurityCode ||
      !!watchAllFields.cardExpire?.month ||
      !!watchAllFields.cardExpire?.year
    );
  }, [watchAllFields]);
  const isButtonDisabled = !isAnyFieldFilled;

  const classesCreditPaymentDesign = registerCreditPaymentDesign({
    isDarkModeEnabled,
    isValid,
  });

  const dispatch = useDispatch();
  const handleRegisterNewCard = async (registerCardInfo: CardInputInfo) => {
    try {
      const response = await getCardTokenGMO({
        cardNo: registerCardInfo.cardNumber,
        expire:
          registerCardInfo.cardExpire.year.toString() +
          registerCardInfo.cardExpire.month.toString(),
        securityCode: registerCardInfo.cardSecurityCode ?? "",
        holderName: registerCardInfo.cardHolderName,
        tokenNumber: 1,
      });

      if (!response.tokens || response.tokens.length === 0) {
        dispatch(modalActions.toggleError({ msg: "カード登録に失敗しました" }));
        return;
      }

      dispatch(
        purchaseActions.registerCardInfo.started({ token: response.tokens[0] })
      );
      setSelectedCardType("registered");
    } catch (error) {
      dispatch(
        modalActions.toggleError({ msg: "システムエラーが発生しました" })
      );
    }
  };

  return (
    <div css={classesCreditPaymentDesign.formContainer}>
      <div css={classesCreditPaymentDesign.inputGroup}>
        <label htmlFor="cardNumber">
          {t("streaming.commerceCart.cardNumber")}
        </label>
        <input
          type="text"
          placeholder="1234123412341234"
          id="cardNumber"
          css={classesCreditPaymentDesign.textInput}
          {...register("cardNumber")}
          inputMode="numeric"
        />
        {errors.cardNumber && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardNumber.message}
          </p>
        )}
        <div css={classesCreditPaymentDesign.brandImageWrapper}>
          <img
            src="/images/liveCommerce/paymentMethodCard.png"
            alt="Card Brands"
            css={classesCreditPaymentDesign.brandImage}
          />
        </div>
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label htmlFor="cardExpireMonth">
          {t("streaming.commerceCart.cardExpiration")}
        </label>
        <div css={classesCreditPaymentDesign.flexRow}>
          <input
            type="text"
            placeholder="MM"
            id="cardExpireMonth"
            aria-label="Month"
            css={classesCreditPaymentDesign.smallInput}
            {...register("cardExpire.month")}
          />
          <input
            type="text"
            placeholder="YY"
            id="cardExpireYear"
            aria-label="Year"
            css={classesCreditPaymentDesign.smallInput}
            {...register("cardExpire.year")}
          />
        </div>
        {errors.cardExpire && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardExpire?.month?.message ??
              errors.cardExpire?.year?.message ??
              errors.cardExpire?.message}
          </p>
        )}
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label htmlFor="cardHolderName">
          {t("streaming.commerceCart.cardHolderName")}
        </label>
        <input
          type="text"
          placeholder="TARO YAMADA"
          id="cardHolderName"
          css={classesCreditPaymentDesign.textInput}
          {...register("cardHolderName")}
        />
        {errors.cardHolderName && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardHolderName.message}
          </p>
        )}
      </div>

      <div css={classesCreditPaymentDesign.inputGroup}>
        <label htmlFor="cardSecurityCode">
          {t("streaming.commerceCart.cardCVV")}
        </label>
        <input
          type="text"
          placeholder="123"
          id="cardSecurityCode"
          css={[
            classesCreditPaymentDesign.textInput,
            css`
              width: 80px;
            `,
          ]}
          {...register("cardSecurityCode")}
        />
        {errors.cardSecurityCode && (
          <p css={classesCreditPaymentDesign.errorText}>
            {errors.cardSecurityCode.message}
          </p>
        )}
      </div>

      <Button
        type="button"
        onClick={handleSubmit(handleRegisterNewCard)}
        css={classesCreditPaymentDesign.registerCardButton}
        disabled={isButtonDisabled}
      >
        {t("streaming.commerceCreditRegister.registerCard")}
      </Button>
    </div>
  );
};

type RegisterCreditStylesProps = {
  isDarkModeEnabled: boolean;
  isValid: boolean;
};

const registerCreditPaymentDesign = (props: RegisterCreditStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };
  const formContainer = css`
    margin-top: 20px;
    padding-left: 20px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 14px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const flexRow = css`
    display: flex;
    gap: 12px;
  `;
  const smallInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    font-size: 16px;
    width: 70px;
    color: ${theme.text};
    &::placeholder {
      color: #a3a3a3;
    }
  `;
  const brandImageWrapper = css`
    margin-top: 8px;
  `;
  const brandImage = css`
    height: 24px;
    object-fit: contain;
  `;
  const textColor = props.isValid ? "#000000" : "#5C5C5C";
  const backgroundColor = props.isValid ? "#FFFFFF" : "#a3a3a3";
  const registerCardButton = css`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${textColor};
    text-align: center;
    margin-top: 16px;
    background-color: ${backgroundColor};
    font-size: 14px;
    font-weight: 600;
    width: fit-content;
    height: 32px;
    border-radius: 6px;
    padding: 0 12px;
  `;

  const errorText = css`
    color: red;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
  `;

  return {
    formContainer,
    inputGroup,
    textInput,
    smallInput,
    flexRow,
    brandImageWrapper,
    brandImage,
    registerCardButton,
    errorText,
  };
};
